/**
 * useProfileData Hook
 *
 * Comprehensive Profile data fetcher with NON-INFLATED counting logic.
 * Implements all Civicbeat Profile requirements.
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface ProfileData {
  // Hero Insight
  heroInsight: {
    text: string;
    topIssues: string[];
    dominantFeeling: string;
  };

  // Key Metrics
  todaysBeat: number; // 0 or 1
  storiesTunedInto: number; // COUNT(DISTINCT story_id)
  emotionalNotesCaptured: number; // COUNT(emotional_reactions)

  // Practice Grid (last 30 days)
  practiceGrid: Array<{
    date: string;
    hasActivity: boolean;
  }>;
  currentStreak: number;

  // Weekly Echo
  weeklyEcho: {
    show: boolean;
    text: string;
  };

  // Emotional Pulse (This Month)
  emotionalPulseMonth: {
    moved: number;
    empowered: number;
    curious: number;
    heavy: number;
    insightText: string;
  };

  // Interests
  interests: string[];

  // Civic Identity (Lifetime)
  civicIdentityLifetime: {
    resonates: number;
    worried: number;
    curious: number;
  };

  // Monthly Pulse Deep
  monthlyPulseDeep: {
    moved: number;
    empowered: number;
    curious: number;
    heavy: number;
    total: number;
    insightText: string;
  };

  // Civic Level System
  civicLevel: string;
  xp: number;
  xpProgress: number;

  isLoading: boolean;
  error: Error | null;
}

export function useProfileData(userId: string | null, refreshKey?: number): ProfileData {
  const [data, setData] = useState<ProfileData>({
    heroInsight: {
      text: "Every song you explore adds another note to your civic story.",
      topIssues: [],
      dominantFeeling: '',
    },
    todaysBeat: 0,
    storiesTunedInto: 0,
    emotionalNotesCaptured: 0,
    practiceGrid: [],
    currentStreak: 0,
    weeklyEcho: {
      show: false,
      text: '',
    },
    emotionalPulseMonth: {
      moved: 0,
      empowered: 0,
      curious: 0,
      heavy: 0,
      insightText: '',
    },
    interests: [],
    civicIdentityLifetime: {
      resonates: 0,
      worried: 0,
      curious: 0,
    },
    monthlyPulseDeep: {
      moved: 0,
      empowered: 0,
      curious: 0,
      heavy: 0,
      total: 0,
      insightText: '',
    },
    civicLevel: 'Observer',
    xp: 0,
    xpProgress: 0,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    console.log('[useProfileData] Effect triggered - userId:', userId, 'refreshKey:', refreshKey);

    if (!userId) {
      setData(prev => ({ ...prev, isLoading: false }));
      return;
    }

    // Fetch immediately on mount
    console.log('[useProfileData] Fetching profile data...');
    fetchProfileData();

    // Also refetch when tab becomes visible (user returns to profile after making interactions)
    const handleVisibilityChange = () => {
      if (!document.hidden && userId) {
        fetchProfileData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    async function fetchProfileData() {
      try {
        // Fetch all data in parallel
        const [
          interactionsResult,
          feelingsResult,
          dailyActivityResult,
          profileResult,
          identityReactionsResult,
          userInterestsResult,
        ] = await Promise.all([
          // Distinct story interactions
          supabase
            .from('user_feelings')
            .select('story_id, created_at, issue_tags')
            .eq('user_id', userId),

          // Emotional reactions
          supabase
            .from('user_feelings')
            .select('feeling_type, created_at, issue_tags')
            .eq('user_id', userId),

          // Daily activity
          supabase
            .from('user_daily_activity')
            .select('date, engagement_count')
            .eq('user_id', userId)
            .order('date', { ascending: false })
            .limit(30),

          // Profile civic level data
          supabase
            .from('profiles')
            .select('civic_level, xp, xp_progress')
            .eq('id', userId)
            .single()
            .then(result => {
              console.log('[useProfileData] Profile XP from DB:', result.data);
              return result;
            }),

          // Identity reactions (for civic identity deep view)
          supabase
            .from('user_interactions')
            .select('type, meta, created_at')
            .eq('user_id', userId)
            .eq('type', 'identity_reaction'),

          // User interests from junction table
          supabase
            .from('user_interests')
            .select('issue_tag')
            .eq('user_id', userId),
        ]);

        if (interactionsResult.error) throw interactionsResult.error;
        if (feelingsResult.error) throw feelingsResult.error;

        const interactions = interactionsResult.data || [];
        const feelings = feelingsResult.data || [];
        const dailyActivity = dailyActivityResult.data || [];
        const profile = profileResult.data;
        const identityReactions = identityReactionsResult.data || [];
        const userInterests = userInterestsResult.data || [];

        // Calculate metrics
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        // TODAY'S BEAT: 0 or 1
        const todayInteractions = interactions.filter(i =>
          i.created_at && i.created_at.startsWith(today)
        );
        const todaysBeat = todayInteractions.length > 0 ? 1 : 0;

        // STORIES TUNED INTO: COUNT(DISTINCT story_id)
        const uniqueStoryIds = new Set(
          interactions.map(i => i.story_id).filter(Boolean)
        );
        const storiesTunedInto = uniqueStoryIds.size;

        // EMOTIONAL NOTES CAPTURED: COUNT(reactions)
        const emotionalNotesCaptured = feelings.length;

        // PRACTICE GRID: Last 30 days
        console.log('[useProfileData] dailyActivity from DB:', dailyActivity);
        const practiceGrid: Array<{ date: string; hasActivity: boolean }> = [];
        for (let i = 29; i >= 0; i--) {
          const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
          const dateStr = date.toISOString().split('T')[0];
          const dayActivity = dailyActivity.find(d => d.date === dateStr);
          console.log(`[useProfileData] Checking ${dateStr}: found=${!!dayActivity}, count=${dayActivity?.engagement_count}`);
          practiceGrid.push({
            date: dateStr,
            hasActivity: dayActivity ? dayActivity.engagement_count > 0 : false,
          });
        }
        console.log('[useProfileData] Final practiceGrid:', practiceGrid);

        // Calculate streak
        let currentStreak = 0;
        for (let i = practiceGrid.length - 1; i >= 0; i--) {
          if (practiceGrid[i].hasActivity) {
            currentStreak++;
          } else {
            break;
          }
        }

        // WEEKLY ECHO: Only show if >= 3 interactions this week
        const weeklyInteractions = interactions.filter(i =>
          i.created_at && new Date(i.created_at) >= sevenDaysAgo
        );
        const weeklyUniqueStories = new Set(
          weeklyInteractions.map(i => i.story_id).filter(Boolean)
        );

        let weeklyEcho = { show: false, text: '' };
        if (weeklyUniqueStories.size >= 3) {
          // Find dominant theme
          const weeklyIssues = new Map<string, number>();
          weeklyInteractions.forEach(i => {
            if (i.issue_tags) {
              i.issue_tags.forEach((tag: string) => {
                weeklyIssues.set(tag, (weeklyIssues.get(tag) || 0) + 1);
              });
            }
          });

          const topIssue = Array.from(weeklyIssues.entries())
            .sort((a, b) => b[1] - a[1])[0];

          if (topIssue) {
            weeklyEcho = {
              show: true,
              text: `Your week has been centered around ${topIssue[0]}.`,
            };
          }
        }

        // EMOTIONAL PULSE (THIS MONTH)
        const monthlyFeelings = feelings.filter(f =>
          f.created_at && new Date(f.created_at) >= monthStart
        );

        const emotionalPulseMonth = {
          moved: monthlyFeelings.filter(f => f.feeling_type === 'moved').length,
          empowered: monthlyFeelings.filter(f => f.feeling_type === 'empowered').length,
          curious: monthlyFeelings.filter(f => f.feeling_type === 'curious').length,
          heavy: monthlyFeelings.filter(f => f.feeling_type === 'heavy').length,
          insightText: generateEmotionalInsight(monthlyFeelings),
        };

        // INTERESTS (from user_interests junction table)
        const interests = userInterests.map(i => i.issue_tag);

        // Calculate issue map and feeling counts (used by multiple sections)
        const issueMap = new Map<string, number>();
        interactions.forEach(i => {
          if (i.issue_tags) {
            i.issue_tags.forEach((tag: string) => {
              issueMap.set(tag, (issueMap.get(tag) || 0) + 1);
            });
          }
        });

        const feelingCounts = {
          moved: feelings.filter(f => f.feeling_type === 'moved').length,
          empowered: feelings.filter(f => f.feeling_type === 'empowered').length,
          curious: feelings.filter(f => f.feeling_type === 'curious').length,
          heavy: feelings.filter(f => f.feeling_type === 'heavy').length,
        };

        // CIVIC IDENTITY (LIFETIME) - Count identity reactions from user_interactions
        const civicIdentityLifetime = {
          resonates: identityReactions.filter((i: any) => i.meta?.reaction === 'resonates').length,
          worried: identityReactions.filter((i: any) => i.meta?.reaction === 'worried').length,
          curious: identityReactions.filter((i: any) => i.meta?.reaction === 'curious').length,
        };

        // MONTHLY PULSE DEEP
        const monthlyPulseDeep = {
          ...emotionalPulseMonth,
          total: monthlyFeelings.length,
          insightText: generateMonthlyDeepInsight(emotionalPulseMonth),
        };

        // HERO INSIGHT
        const topIssuesForHero = Array.from(issueMap.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([issue]) => issue);

        const dominantFeelingForHero = Object.entries(feelingCounts)
          .sort((a, b) => b[1] - a[1])[0]?.[0] || '';

        const heroInsight = generateHeroInsight(topIssuesForHero, dominantFeelingForHero);

        console.log('[useProfileData] Setting profile state - XP:', profile?.xp, 'Level:', profile?.civic_level);

        setData({
          heroInsight,
          todaysBeat,
          storiesTunedInto,
          emotionalNotesCaptured,
          practiceGrid,
          currentStreak,
          weeklyEcho,
          emotionalPulseMonth,
          interests,
          civicIdentityLifetime,
          monthlyPulseDeep,
          civicLevel: profile?.civic_level || 'Observer',
          xp: profile?.xp || 0,
          xpProgress: profile?.xp_progress || 0,
          isLoading: false,
          error: null,
        });

        console.log('[useProfileData] ✅ Profile data updated successfully');
      } catch (error) {
        console.error('[useProfileData] Error:', error);
        setData(prev => ({
          ...prev,
          isLoading: false,
          error: error as Error,
        }));
      }
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, refreshKey]);

  return data;
}

// Helper functions
function generateHeroInsight(topIssues: string[], dominantFeeling: string) {
  if (topIssues.length >= 2 && dominantFeeling) {
    return {
      text: `You often feel ${dominantFeeling} when you explore stories — that emotional thread is becoming part of your civic rhythm.`,
      topIssues: topIssues.slice(0, 3),
      dominantFeeling,
    };
  }

  return {
    text: "Every song you explore adds another note to your civic story.",
    topIssues: topIssues.slice(0, 3),
    dominantFeeling,
  };
}

function generateEmotionalInsight(feelings: any[]) {
  if (feelings.length === 0) return '';

  const counts = {
    moved: feelings.filter(f => f.feeling_type === 'moved').length,
    empowered: feelings.filter(f => f.feeling_type === 'empowered').length,
    curious: feelings.filter(f => f.feeling_type === 'curious').length,
    heavy: feelings.filter(f => f.feeling_type === 'heavy').length,
  };

  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const top = sorted[0][0];
  const second = sorted[1][0];

  return `Your civic pulse this month is driven by ${top}, followed by ${second}.`;
}

function generateMonthlyDeepInsight(pulse: any) {
  const total = pulse.moved + pulse.empowered + pulse.curious + pulse.heavy;
  if (total === 0) return '';

  const sorted = Object.entries(pulse)
    .filter(([key]) => key !== 'insightText')
    .sort((a: any, b: any) => b[1] - a[1]);

  const top = sorted[0][0];

  return `Your month in feelings shows a strong pattern: ${top}.`;
}
